import styled from '@emotion/styled'
import { Button } from '@retailer-platform/shared-components/src/tds'
import { ShownIcon } from '@instacart/ids-core'
import { Loader } from '@retailer-platform/shared-components/src/common/loader/Loader'
import { FormattedMessage } from 'react-intl'
import { NotificationLevel, notify } from '@retailer-platform/shared-components'
import { useCurrentAccountContext } from '../../../utils/current-account/CurrentAccountContext'
import { MENU_BAR_Z_INDEX } from '../nav-v2/components/navBar.constants'
import { useStopImpersonationSessionMutation } from '../../../__codegen__/api'
import { useNavigateToPath } from '../../../utils/routing/navigation.hooks'
import { useDashMessages } from '../../../intl/intl.hooks'
import { useTrackEvent } from '../../../utils/events/useTrackEvent.hook'
import { useCoreContext } from '../../../utils/core/RPPCoreContext'
import { url } from '../../../utils/parsing/url'
import { useImpersonationState } from './ImpersonationState.hooks'

export const IMPERSONATION_BANNER_HEIGHT = 72

const Banner = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '16px',
  gap: '16px',

  width: '100vw',
  height: `${IMPERSONATION_BANNER_HEIGHT}px`,
  background: '#F89F1A',
  zIndex: MENU_BAR_Z_INDEX + 1,
  position: 'relative',
})

export const useImpersonationBannerHeight = (): number => {
  const accountContext = useCurrentAccountContext()
  return accountContext?.account?.isImpersonating ? IMPERSONATION_BANNER_HEIGHT : 0
}

const ButtonContainer = styled.div({
  marginLeft: 'auto',
  padding: '16px',
})

const TextContainer = styled.span(({ strong }: { strong?: boolean }) => ({
  height: '20px',
  fontStyle: 'normal',
  fontWeight: strong ? 700 : 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: '#242529',
}))

export const ImpersonationStatusBanner = () => {
  const accountContext = useCurrentAccountContext()
  const { routesByName } = useCoreContext()

  const trackEvent = useTrackEvent()

  const name = accountContext?.account?.givenName || ''

  const [triggerMutation, { data, loading }] = useStopImpersonationSessionMutation()

  const { impersonationData } = useImpersonationState()

  const messages = useDashMessages(
    {
      quitImpersonation: 'impersonationStatusBanner.quitImpersonation',
      impersonateAccountNotifyError: 'impersonationStatusBanner.quitImpersonationNotifyError',
    },
    {
      name,
    }
  )
  const defaultPath = url(routesByName['app-admin-account-management-list'])
  const navigateToPath = useNavigateToPath(impersonationData?.pathOrigin || defaultPath)

  const onQuitImpersonation = () => {
    triggerMutation({
      onCompleted(data) {
        if (data?.enterpriseAccountDisableImpersonation?.success) {
          const result = trackEvent({
            id: 'account_management.impersonate_account.end',
            description: 'Impersonate user session ended',
            data: {
              targetUser: accountContext.account?.canonicalId,
            },
          })

          if (result instanceof Promise) {
            // wait until analytics event is sent before hard reloading the page
            result.finally(() => {
              navigateToPath()
            })
          } else {
            // result is void, navigate immediately
            navigateToPath()
          }
        }
      },
      onError() {
        notify({
          contents: messages.impersonateAccountNotifyError,
          level: NotificationLevel.Error,
        })
      },
    })
  }

  if (!accountContext) {
    return null
  }

  const isImpersonationSession = accountContext.account?.isImpersonating

  if (!isImpersonationSession) {
    return null
  }

  return (
    <>
      <Banner>
        <ShownIcon size={24} />
        <TextContainer>
          <FormattedMessage
            id="impersonationStatusBanner.text"
            values={{
              b: chunks => <TextContainer strong>{chunks}</TextContainer>,
              name,
            }}
          />
        </TextContainer>
        <ButtonContainer>
          <Button variant="secondary" onClick={onQuitImpersonation}>
            {messages.quitImpersonation}
          </Button>
        </ButtonContainer>
      </Banner>
      {loading && <Loader />}
    </>
  )
}
